"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Bell, Clock, ChevronRight, Check, Loader, CircleAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={cn("flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className)} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div className="relative">
        <select className={cn("flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none", className)} ref={ref} {...props}>
          {children}
        </select>
        <ChevronRight className="absolute right-3 top-3 h-4 w-4 rotate-90 opacity-50 pointer-events-none" />
      </div>;
});
Select.displayName = "Select";
const Label = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLLabelElement>) => <label className={cn("text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", className)} {...props}>
    {children}
  </label>;
function ReleaseForm({
  productName
}: {
  productName: string;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit
  } = useWvcForm();
  const {
    register
  } = useWvcForm() as any;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary">
          <Check className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">You're on the list!</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Close
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name</Label>
          <Input id="first_name" placeholder="e.g., Alex" {...register("first_name", {
          required: true
        })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name</Label>
          <Input id="last_name" placeholder="e.g., Rivera" {...register("last_name", {
          required: true
        })} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input id="email" type="email" placeholder="you@example.com" {...register("email", {
        required: true,
        pattern: /^\S+@\S+$/i
      })} />
        <p className="text-[0.8rem] text-muted-foreground">We'll send release alerts for {productName}.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="interest_category">Shoe Category of Interest</Label>
        <Select id="interest_category" {...register("interest_category")}>
          <option value="" disabled selected>Select a category</option>
          <option value="Running">Running</option>
          <option value="Training">Training</option>
          <option value="Basketball">Basketball</option>
          <option value="Casual">Casual</option>
          <option value="Limited-Edition">Limited-Edition</option>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number (optional)</Label>
        <Input id="phone" type="tel" placeholder="+1 555-123-4567" {...register("phone")} />
        <p className="text-[0.8rem] text-muted-foreground">For SMS alerts (US format)</p>
      </div>

      {submitError && <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
          <CircleAlert className="h-4 w-4" />
          {submitError}
        </div>}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Signing Up...
          </> : "Notify Me"}
      </Button>
    </form>;
}
function ReleaseItem({
  post,
  index
}: {
  post: any;
  index: number;
}) {
  const date = new Date(post.date || Date.now());
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  const year = date.getFullYear();
  const imageSrc = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/generic_shoe_placeholder.png";
  return <div className="relative pl-8 md:pl-12 py-6 group" data-index={index}>
      {}
      <div className="absolute left-0 top-8 -translate-x-[5px] md:-translate-x-[4px] h-3 w-3 rounded-full bg-primary ring-4 ring-background z-10 group-hover:scale-125 transition-transform duration-300 shadow-[0_0_10px_rgba(var(--primary),0.5)]" />
      
      {}
      <div className="md:hidden mb-2 flex items-center gap-2 text-primary font-mono text-sm">
        <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
        <span>{formattedDate}, {year}</span>
      </div>

      <Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300">
        <div className="grid md:grid-cols-[200px_1fr] gap-0">
          {}
          <div className="relative h-48 md:h-auto overflow-hidden bg-muted">
            <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-overlay" />
            <img src={imageSrc} alt={post.title} className="h-full w-full object-cover filter blur-sm scale-110 group-hover:blur-none group-hover:scale-100 transition-all duration-700 ease-out" />
            {}
            <div className="hidden md:flex absolute top-0 left-0 bg-primary text-primary-foreground px-3 py-1 font-mono text-sm font-bold z-20">
              {formattedDate}
            </div>
          </div>

          {}
          <div className="p-6 flex flex-col justify-between gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                  Upcoming
                </span>
              </div>
              <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                {post.excerpt ? post.excerpt.replace(/<[^>]*>/g, '') : "Get ready for the next big drop. Limited quantities available for this exclusive release."}
              </p>
            </div>

            <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/50">
              <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" /> 10:00 AM EST
                </span>
              </div>

              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-2 shadow-[0_0_15px_rgba(var(--primary),0.3)] hover:shadow-[0_0_20px_rgba(var(--primary),0.5)] transition-shadow">
                    <Bell className="h-3 w-3" />
                    Remind Me
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px] bg-card border-border text-foreground">
                  <DialogHeader>
                    <DialogTitle>Get Notified</DialogTitle>
                    <DialogDescription>
                      Sign up for release alerts for <span className="text-primary font-semibold">{post.title}</span>.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <FormProvider formId="release_calendar_signup_limited_drops" sectionName="future-release-calendar" defaultValues={{
                  first_name: "",
                  last_name: "",
                  email: "",
                  interest_category: "",
                  phone: ""
                }} validationRules={{
                  first_name: {
                    required: true
                  },
                  last_name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  interest_category: {
                    required: false
                  },
                  phone: {
                    required: false
                  }
                }} submitText="Notify Me" successMessage="You've been added to the notification list for this drop.">
                    <ReleaseForm productName={post.title} />
                  </FormProvider>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}
function ReleaseTimeline() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="space-y-8 pl-12 relative">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-border/30" />
        {[...Array(3)].map((_, i) => <div key={i} className="relative" data-index={i}>
            <div className="absolute left-0 top-8 -translate-x-[5px] h-3 w-3 rounded-full bg-muted" />
            <div className="h-48 rounded-xl bg-muted/10 animate-pulse border border-border/30" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 border border-destructive/20 rounded-xl bg-destructive/5">
        <p className="text-destructive mb-4">Failed to load release calendar.</p>
        <Button onClick={refetch} variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10">
          Retry Connection
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-16 border border-dashed border-border rounded-xl">
        <SafeHydrate><Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" /></SafeHydrate>
        <h3 className="text-lg font-semibold">No Upcoming Releases</h3>
        <p className="text-muted-foreground">Check back soon for new drops.</p>
      </div>;
  }
  return <div className="relative">
      {}
      <div className="absolute left-[3px] md:left-[3px] top-4 bottom-4 w-px bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" />
      
      <div className="space-y-8">
        {posts.map((post, i) => <ReleaseItem key={post.id} post={post} index={i} />)}
      </div>
    </div>;
}
export default function FutureReleaseCalendar() {
  const wp_query_params = {
    per_page: 5,
    status: "publish",
    orderby: "date",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="future-release-calendar" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-20 select-none" style={{
      backgroundImage: "linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06))",
      backgroundSize: "100% 4px, 6px 100%"
    }} />
      
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="container relative z-10 px-4 md:px-6 mx-auto max-w-4xl">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono uppercase tracking-wider">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Live Sync
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
            Future Release Calendar
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Stay ahead of the game. Track upcoming limited-edition drops and set reminders so you never miss a release.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 5,
        status: ["publish"],
        orderby: "date",
        order: "asc"
      }} className="relative min-h-[400px]">
          <ReleaseTimeline />
        </PostsProvider>

        <div className="mt-16 text-center">
          <Link to="/limited-drops">
            <Button variant="outline" className="border-primary/20 hover:bg-primary/10 hover:text-primary transition-all">
              View All Drops <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}